import {
  abs,
  clamp,
  dFdx,
  dFdy,
  dot,
  Fn,
  float,
  If,
  max,
  min,
  mix,
  normalize,
  select,
  step,
  struct,
  texture,
  vec2,
  vec3,
} from 'three/tsl';
import type * as THREE from 'three/webgpu';

type N = any;

/**
 * Detail near the player from real textures (scanned ground, rock, sand, snow): their small-scale
 * variation and relief laid over the palette's colour, which stays the same at any distance.
 * The palette says how much of each material is where (LandSurface.detail).
 */
export interface DetailMaterial {
  /** Colour map (sRGB) and its OpenGL-style normal map. */
  albedo: THREE.Texture;
  normal: THREE.Texture;
  /** Ground one repeat of the textures covers (m); must divide DETAIL_PERIOD. */
  size: number;
  /** The colour map's mean (linear RGB): the detail varies around it, so the palette's colour stays. */
  mean: [number, number, number];
  /** How much of its light and dark shows (0 … 1), and how strong its relief is. */
  strength: number;
  bump: number;
}

export interface TerrainDetail {
  ground: DetailMaterial;
  rock: DetailMaterial;
  sand: DetailMaterial;
  snow: DetailMaterial;
}

/**
 * Every detail texture repeats within this many metres, so texture coordinates can be taken
 * from the camera's universe position modulo it (in f64 on the CPU) plus the camera-relative
 * position: small numbers, precise in f32 anywhere in the universe.
 */
export const DETAIL_PERIOD = 64;

/** Share of each material's own colour variation kept (the rest is its brightness only). */
const HUE = 0.35;
/**
 * The colour is also sampled this many times larger, and multiplied in (this much of it): the
 * two repeats never line up, so a texture's blotches don't draw a grid across open ground.
 */
const WIDE = 3.7;
const WIDE_SHARE = 0.6;

const DetailOut = struct({ factor: 'vec3', normal: 'vec3' });

/**
 * Detail from the materials at a point: a factor for the palette's albedo (about 1 on average)
 * and the surface normal with the materials' relief. `P` is the position in metres, periodic
 * (see DETAIL_PERIOD); `n` the surface normal (world space, unit); `weights` the materials'
 * shares (x ground, y rock, z sand, w snow); `fade` how much of the detail shows (1 close, 0
 * far). Materials with no share here, and anything far away, sample nothing.
 *
 * The textures are sampled along the two world axes the surface faces most (biplanar mapping,
 * after Inigo Quilez): on a hollow's inner surface "up" can point anywhere. Normal maps are
 * turned into world space by a whiteout blend (their slopes add to the surface's).
 */
export function detailAt(detail: TerrainDetail, P: N, n: N, weights: N, fade: N): { albedo: N; normal: N } {
  const out = Fn(() => {
    // Screen derivatives are illegal in branches, so they're taken here. The planes' coordinates
    // are worked out where each sample needs them: a value (even a .toVar()) first built in one
    // material's branch may be computed only there and read as zero in the next. The gradients
    // were, so rock, sand and snow away from any grass were sampled unfiltered, and glittered.
    const p = vec3(P).toVar();
    const nv = vec3(n).toVar();
    // Assigned, not just .toVar(): TSL put a bare dFdx(p).toVar() inside the first branch that
    // read it, where it's both illegal and never run for the other materials.
    const dPdx = vec3(0).toVar();
    dPdx.assign(dFdx(p));
    const dPdy = vec3(0).toVar();
    dPdy.assign(dFdy(p));
    const na = abs(nv).toVar();
    // The axis the surface faces most (ma), least (mi) and the one between (me): 0 x, 1 y, 2 z.
    const ma = select(
      na.x.greaterThanEqual(na.y).and(na.x.greaterThanEqual(na.z)),
      float(0),
      select(na.y.greaterThanEqual(na.z), float(1), float(2)),
    ).toVar();
    const mi = select(
      na.x.lessThan(na.y).and(na.x.lessThan(na.z)),
      float(0),
      select(na.y.lessThan(na.z), float(1), float(2)),
    );
    const me = float(3).sub(ma).sub(mi).toVar();
    // A plane by masks (TSL's select() becomes if/else, which repeated a sample in each arm):
    // e0, e1, e2 pick the axis, and the back sides' coordinates are mirrored so the textures
    // don't read backwards there.
    const planeOf = (k: N) => {
      const e0 = float(1).sub(step(0.5, k));
      const e2 = step(1.5, k);
      const e1 = float(1).sub(e0).sub(e2);
      const up = step(0, nv.x.mul(e0).add(nv.y.mul(e1)).add(nv.z.mul(e2)));
      // x and y planes mirror their back sides, the z plane its front.
      const flip = up
        .mul(2)
        .sub(1)
        .mul(float(1).sub(e2.mul(2)));
      return { e0, e1, e2, flip };
    };
    type Plane = ReturnType<typeof planeOf>;
    // Plane coordinates: x → (z, y), y → (x, z), z → (x, y).
    const on = (pl: Plane, v: N) =>
      vec2(
        v.z
          .mul(pl.e0)
          .add(v.x.mul(pl.e1.add(pl.e2)))
          .mul(pl.flip),
        v.y.mul(pl.e0.add(pl.e2)).add(v.z.mul(pl.e1)),
      );
    const pick = (k: N, v: N) => select(k.equal(0), v.x, select(k.equal(1), v.y, v.z));
    const weight = (w: N) =>
      clamp(w.sub(0.5773).div(1 - 0.5773), 0, 1)
        .add(1e-4)
        .toVar();
    const A = planeOf(ma);
    const B = planeOf(me);
    // The second plane's share. Over most of a hollow it's nothing (only near a diagonal of the
    // world axes do two planes both count), and then it isn't sampled at all.
    const wA = weight(pick(ma, na));
    const wB = weight(pick(me, na));
    const bShare = wB.div(wA.add(wB)).toVar();

    const factor = vec3(1).toVar();
    const normal = vec3(nv).toVar();
    const mats: [DetailMaterial, N][] = [
      [detail.ground, weights.x],
      [detail.rock, weights.y],
      [detail.sand, weights.z],
      [detail.snow, weights.w],
    ];
    for (const [m, w] of mats) {
      const share = w.mul(fade).toVar();
      If(share.greaterThan(0.02), () => {
        const s = 1 / m.size;
        const sample = (tex: THREE.Texture, pl: Plane, k = s) =>
          texture(tex, on(pl, p).mul(k)).grad(on(pl, dPdx).mul(k), on(pl, dPdy).mul(k));
        // Relief: a plane's normal map, scaled by the bump, into world space. Plane x gives
        // (tz·n.x, ty + n.y, tx + n.z), y (tx + n.x, tz·n.y, ty + n.z), z (tx + n.x, ty + n.y, tz·n.z).
        const world = (pl: Plane) => {
          const t = sample(m.normal, pl).xyz.mul(2).sub(1);
          const tx = t.x.mul(m.bump).mul(pl.flip);
          const ty = t.y.mul(m.bump);
          const tz = abs(t.z);
          return vec3(
            mix(tx.add(nv.x), tz.mul(nv.x), pl.e0),
            mix(ty.add(nv.y), tz.mul(nv.y), pl.e1),
            mix(tx.mul(pl.e0).add(ty.mul(pl.e1)).add(nv.z), tz.mul(nv.z), pl.e2),
          );
        };
        // Colour at two sizes (so its repeats don't line up) and relief, from the main plane,
        // then blended with the second plane's where it counts.
        const col = sample(m.albedo, A).xyz.toVar();
        const colWide = sample(m.albedo, A, s / WIDE).xyz.toVar();
        const rel = world(A).toVar();
        If(bShare.greaterThan(0.02), () => {
          col.assign(mix(col, sample(m.albedo, B).xyz, bShare));
          colWide.assign(mix(colWide, sample(m.albedo, B, s / WIDE).xyz, bShare));
          rel.assign(mix(rel, world(B), bShare));
        });
        // Colour: its brightness against the mean, and a little of its own hue.
        const mean = vec3(...m.mean);
        const lum = (c: N) => dot(c, vec3(0.2126, 0.7152, 0.0722));
        const vary = (c: N) => mix(vec3(lum(c).div(lum(mean))), c.div(mean), HUE);
        // Within limits: a bright pebble on dark moss would otherwise flash five times as bright.
        const f = clamp(vary(col).mul(mix(vec3(1), vary(colWide), WIDE_SHARE)), 0.35, 1.8);
        factor.addAssign(f.sub(1).mul(m.strength).mul(share));
        normal.addAssign(normalize(rel).sub(nv).mul(min(share, 1)));
      });
    }
    return DetailOut(max(factor, vec3(0)), normalize(normal));
  })();
  return { albedo: out.get('factor'), normal: out.get('normal') };
}
